// The business's payment QR as it is shown (docs/plans/payment-qr.md): a tile that is
// always black on white, the card in the checkout, and the full screen turned towards
// the customer. What the code says comes from `paymentQrOf(settings)`.
import { Button, Modal, cx } from '@ims/ui'
import { CheckCircle2, Info, Maximize, QrCode as QrIcon } from '@ims/ui/icons'
import type { PaymentQr } from './lib/payment-qr.ts'
import { QrCode } from './qr.tsx'

/**
 * The code on a white tile. White in the dark theme too: a phone cannot read a QR drawn
 * light on dark, so these two colours are the one place the theme tokens do not apply.
 */
export function PaymentQrTile({ payload, className }: { payload: string; className?: string }) {
  return (
    <span
      className={cx(
        'block shrink-0 overflow-hidden rounded-xl border border-[#e5e7eb] bg-[#fff]',
        className,
      )}
    >
      <QrCode value={payload} label="Payment QR" />
    </span>
  )
}

/** Under the Complete button while a QR payment is chosen: the app cannot see the money. */
export function PaymentQrReminder({ className }: { className?: string }) {
  return (
    <p className={cx('flex items-center justify-center gap-1.5 text-xs text-muted', className)}>
      <Info className="size-4 shrink-0" aria-hidden />
      Complete the sale once the money shows on your phone.
    </p>
  )
}

/** In the checkout, under the payment buttons: the code, the amount and "Show to customer". */
export function PaymentQrCard({
  qr,
  amount,
  onShow,
}: {
  qr: PaymentQr
  /** The amount to pay, as text ("Rs. 1,480.00"). */
  amount: string
  onShow: () => void
}) {
  return (
    <section
      aria-label="Pay by QR"
      className="flex items-center gap-3 rounded-card border border-sell/30 bg-sell-wash p-3 lg:flex-col lg:p-3.5 lg:text-center"
    >
      <PaymentQrTile payload={qr.payload} className="w-26 lg:w-44" />
      <div className="min-w-0">
        <p className="text-section text-sell">Scan to pay</p>
        <p className="tabular text-2xl font-bold tracking-tight">{amount}</p>
        <p className="mt-0.5 text-xs text-muted">
          <span translate="no">{qr.label}</span>
          {qr.payee && (
            <>
              {' · '}
              <span translate="no">{qr.payee}</span>
            </>
          )}
        </p>
        <Button
          variant="secondary"
          className="mt-2"
          icon={<Maximize className="size-4" aria-hidden />}
          onClick={onShow}
        >
          Show to customer
        </Button>
      </div>
    </section>
  )
}

/** The whole window, to turn towards the customer: the amount, the code, who is paid. */
export function PaymentQrScreen({
  qr,
  amount,
  enter,
  context,
  busy,
  onReceived,
  onClose,
}: {
  qr: PaymentQr
  amount: string
  /** The number the customer types in their own app ("1,480.00"). */
  enter: string
  /** What is being paid for ("Table 4"), when there is a name for it. */
  context?: string
  busy?: boolean
  /** Left out where the sale cannot be completed from here. */
  onReceived?: () => void
  onClose: () => void
}) {
  return (
    <Modal open onClose={onClose} size="full" title={context ?? 'Payment'}>
      <div className="mx-auto flex min-h-full max-w-md flex-col items-center text-center">
        <p className="text-section text-sell">Scan to pay</p>
        <p className="tabular mt-1 text-[2.75rem] leading-tight font-bold tracking-tight">
          {amount}
        </p>
        <span className="mt-4 block w-full max-w-80 rounded-[1.6rem] bg-[#fff] p-2.5 shadow-modal ring-1 ring-border">
          <PaymentQrTile payload={qr.payload} className="w-full rounded-card border-0" />
        </span>
        <span className="mt-4 inline-flex h-7 items-center gap-1.5 rounded-full bg-sell-light px-3 text-sm font-bold text-sell">
          <QrIcon className="size-4" aria-hidden />
          <span translate="no">{qr.label}</span>
        </span>
        {qr.payee && (
          <p className="mt-1.5 text-lg font-bold tracking-tight" translate="no">
            {qr.payee}
          </p>
        )}
        <p className="mt-1 text-sm text-muted">
          Enter <b className="tabular text-ink">{enter}</b> in your app
        </p>
        <div className="min-h-6 flex-1" />
        <div className="grid w-full gap-2 pb-[max(0.25rem,env(safe-area-inset-bottom))]">
          {onReceived && (
            <Button
              size="lg"
              className="w-full"
              loading={busy}
              icon={<CheckCircle2 className="size-5" aria-hidden />}
              onClick={onReceived}
            >
              Received · complete sale
            </Button>
          )}
          <Button variant="secondary" size="lg" className="w-full" onClick={onClose}>
            Back to the bill
          </Button>
        </div>
      </div>
    </Modal>
  )
}
